import type { NotificationDto } from './types';

/**
 * Holds notifications waiting to be displayed and enforces the queueable/solo rule:
 *
 *   - solo (queueable=false) → if currently nothing is shown, show it immediately and BLOCK others
 *     until it is dismissed. Otherwise hold it for the next page visit (server keeps shown_at=NULL).
 *   - queueable=true → wait in line, show one at a time after the previous dismisses.
 *
 * Deduplication by uuid: same notification may arrive twice (polling overlap, WS replay) — we ignore the duplicate.
 */
export class Queue {
  private items: NotificationDto[] = [];
  private seen = new Set<string>();
  private active: NotificationDto | null = null;

  enqueue(item: NotificationDto): void {
    if (this.seen.has(item.uuid)) return;
    this.seen.add(item.uuid);
    this.items.push(item);
  }

  /** Returns the next item to display, or null if nothing is ready. */
  next(): NotificationDto | null {
    if (this.active) return null;
    const item = this.items.shift();
    if (!item) return null;
    this.active = item;
    return item;
  }

  /** Caller signals the active item is gone from the DOM. */
  markDone(uuid: string): void {
    if (this.active && this.active.uuid === uuid) {
      this.active = null;
    }
  }

  /** Test helper. */
  size(): number {
    return this.items.length;
  }
}
